import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { SubscriptionState } from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { subscriptionRepoFor } from '../lib/repositories/subscription-repo.js';
import { logger } from '../lib/logger.js';
import { assembleTrialLine, computeTrialCountdown } from '../lib/trial-countdown.js';

/**
 * Operator free-trial indicator (roadmap #11).
 *
 * A CALM, always-present strip at the very top of the dashboard ONLY while the
 * tenant's subscription is `trial`. It shows how many trial days remain and a
 * one-tap CTA to add billing, so nobody's trial silently lapses. Structurally a
 * sibling of PastDueBanner: an in-flow full-width strip (NOT position:fixed —
 * it pushes route content down so it never overlaps a route header), flexWrap +
 * clamp() responsive, `role="status"`, per-session dismiss via sessionStorage.
 *
 * Tone is deliberately positive/neutral (brand-primary tint) — clearly distinct
 * from the past-due amber and the suspended red. Inside the final stretch
 * (`imminent`, <= 3 days) it shifts to a gentle amber nudge, never red.
 *
 * `trial` and `past_due` are mutually-exclusive `TenantStatus` values, so this
 * and PastDueBanner never render at the same time — no stacking to coordinate.
 *
 * Only owner/manager (and super-admin) may read tenants/{tid}/subscription/*
 * per infra/firestore.rules, so the read is role-gated; cashier/washer don't
 * manage billing and simply see nothing here (no action for them to take — that
 * is correct, not a dead-end). Reads a single doc one-shot (no live listener —
 * a whole-day countdown re-derives fine from a per-render Date.now()).
 */
export function TrialBanner() {
  const { t } = useI18n();
  const { user, claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  // Role gate — mirrors past-due-banner's canReadInvoices. subscription/current
  // is readable only by owner/manager/superadmin (infra/firestore.rules).
  const rolesClaim = claims?.['roles'];
  const tenantRole =
    tenantId && rolesClaim && typeof rolesClaim === 'object'
      ? (rolesClaim as Record<string, unknown>)[tenantId]
      : undefined;
  const canReadSubscription =
    tenantRole === 'owner' || tenantRole === 'manager' || Boolean(claims?.['superadmin']);

  // One-shot subscription read (not a live listener). Null until loaded, when
  // the role can't read it, or on a transient failure.
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  useEffect(() => {
    if (!user || !tenantId || !canReadSubscription) {
      setSubscription(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sub = await subscriptionRepoFor(tenantId).get('current');
        if (!cancelled) setSubscription(sub);
      } catch (e) {
        // Stay silent on a transient read failure rather than showing a trial
        // nudge we can't substantiate.
        logger.warn('TrialBanner: subscription load failed', { error: String(e) });
        if (!cancelled) setSubscription(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, tenantId, canReadSubscription]);

  // Per-session, per-tenant dismiss. Reappears next session while still on
  // trial — the state hasn't been resolved, just quietened for now.
  const dismissKey = tenantId ? `kilat.trialDismissed.${tenantId}` : null;
  const [dismissed, setDismissed] = useState<boolean>(false);
  useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(window.sessionStorage.getItem(dismissKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  const countdown = computeTrialCountdown(subscription, Date.now());
  if (!countdown || dismissed) return null;

  const s = countdown.imminent ? NUDGE_STYLES : CALM_STYLES;
  const trialLine = assembleTrialLine(countdown, t, formatDate);

  function dismiss() {
    if (dismissKey) {
      try {
        window.sessionStorage.setItem(dismissKey, '1');
      } catch {
        /* swallow — dismiss is best-effort */
      }
    }
    setDismissed(true);
  }

  return (
    // ONE line, not a 160px band. It sits above every screen for the length
    // of a trial, so every pixel it takes is taken from the queue, the till
    // and the numbers an operator opened the app for. The tag + the days left
    // carry the message; the supporting sentence drops on narrow screens
    // (`.k-trial-note` in globals.css) rather than wrapping the strip taller.
    <div role="status" data-shell-banner style={s.strip}>
      <div style={textColStyle}>
        <span style={s.eyebrow}>{t('dashboard.trial.eyebrow')}</span>
        {trialLine ? <span style={s.title}>{trialLine}</span> : null}
        <span className="k-trial-note" style={s.body}>
          {t('dashboard.trial.body')}
        </span>
      </div>
      <div style={actionColStyle}>
        <Link to="/billing" style={s.cta}>
          {t('dashboard.trial.cta')}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('dashboard.trial.dismiss')}
          title={t('dashboard.trial.dismiss')}
          style={s.dismiss}
        >
          {'×'}
        </button>
      </div>
    </div>
  );
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

// ── tones ─────────────────────────────────────────────────────────────
//
// CALM: brand-primary green tint (matches the "active"/"trial" StatusBadge tone
// in routes/billing.tsx — rgba(13,122,79,…) with `var(--color-primary)` text).
// A trial is a positive state, so this is the resting look. NUDGE: the same
// gentle amber as PastDueBanner for the final <=3 days — a timely nudge, never
// the red of the suspended lock. The two banners never render together
// (mutually-exclusive statuses), so sharing the amber is safe.
interface Tone {
  readonly fg: string;
  readonly bg: string;
  readonly border: string;
  readonly ctaBg: string;
  readonly ctaFg: string;
}

// Colour ROLES, not values (CLAUDE.md rule 12) — so the dark theme and any
// re-brand reach this strip too.
const CALM: Tone = {
  fg: 'var(--color-primary)',
  bg: 'var(--color-primary-soft)',
  border: 'color-mix(in srgb, var(--color-primary) 22%, transparent)',
  ctaBg: 'var(--color-primary)',
  ctaFg: 'var(--color-primary-fg)',
};

const NUDGE: Tone = {
  fg: 'var(--color-warn)',
  bg: 'var(--color-warn-bg)',
  border: 'color-mix(in srgb, var(--color-warn) 35%, transparent)',
  ctaBg: 'var(--color-warn)',
  ctaFg: 'var(--color-primary-fg)',
};

interface BannerStyles {
  readonly strip: React.CSSProperties;
  readonly eyebrow: React.CSSProperties;
  readonly title: React.CSSProperties;
  readonly body: React.CSSProperties;
  readonly cta: React.CSSProperties;
  readonly dismiss: React.CSSProperties;
}

// Layout is tone-independent; only the colours change between calm and nudge.
const textColStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--space-3)',
  minWidth: 0,
  flex: '1 1 auto',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const actionColStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flex: '0 0 auto',
};

function buildStyles(tone: Tone): BannerStyles {
  return {
    strip: {
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'var(--space-3)',
      width: '100%',
      boxSizing: 'border-box',
      padding: '4px var(--space-5)',
      minHeight: 'var(--touch-target)',
      background: tone.bg,
      borderBottom: `1px solid ${tone.border}`,
      color: tone.fg,
    },
    eyebrow: {
      flexShrink: 0,
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tone.fg,
      padding: '2px 8px',
      borderRadius: 'var(--radius-pill)',
      border: `1px solid ${tone.border}`,
    },
    title: {
      flexShrink: 0,
      fontSize: 'var(--text-meta)',
      fontWeight: 650,
      color: tone.fg,
    },
    body: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: 'var(--text-meta)',
      color: tone.fg,
      opacity: 0.85,
    },
    cta: {
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 'calc(var(--touch-target) - 8px)',
      background: tone.ctaBg,
      color: tone.ctaFg,
      padding: '0 14px',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-meta)',
      fontWeight: 600,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },
    dismiss: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'var(--touch-target, 32px)',
      height: 'var(--touch-target, 32px)',
      minWidth: 32,
      minHeight: 32,
      border: 'none',
      background: 'transparent',
      color: tone.fg,
      fontSize: '20px',
      lineHeight: 1,
      cursor: 'pointer',
      borderRadius: 'var(--radius-pill)',
    },
  };
}

const CALM_STYLES = buildStyles(CALM);
const NUDGE_STYLES = buildStyles(NUDGE);
